import { Fragment } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import { Divider, Flex, Grid, Heading, Paragraph, spaceMixin } from '@zillow/constellation';
import type { Answers, CurrentIncome, Document, IncomeType } from '../../../../../graphql/queries';
import { getAnswersForFields } from '../../../../../utils/overview/overview-display';
import { FIELD_IDS } from '../../../../../constants/wizard';
import { get } from 'lodash-es';
import { getIncomeTitle } from '../../../../ApplicationEdit/Sections/4IncomeHistory/1CurrentIncomeStep';
import {
  getCurrencyDisplay,
  getFormattedDateValue,
  getNumberFromString,
  getPhoneNumberScreenFormat,
} from '../../../../../utils/formatters';
import { MAX_DOCUMENTS_PER_INCOME } from '../../../../../constants/form';

interface Props {
  answers: Answers;
  isReadOnly?: boolean;
}

const fieldsInStep = [
  FIELD_IDS.CURRENT_INCOMES_TYPE,
  FIELD_IDS.CURRENT_INCOMES_AMOUNT,
  FIELD_IDS.CURRENT_INCOMES_TITLE,
  FIELD_IDS.CURRENT_INCOMES_ORGANIZATION,
  FIELD_IDS.CURRENT_INCOMES_START_DATE,
  FIELD_IDS.CURRENT_INCOMES_EMPLOYER_NAME,
  FIELD_IDS.CURRENT_INCOMES_EMPLOYER_EMAIL,
  FIELD_IDS.CURRENT_INCOMES_EMPLOYER_PHONE,
  FIELD_IDS.CURRENT_INCOMES_NOTE,
  FIELD_IDS.CURRENT_INCOMES_DOCUMENTS,
];

const FlexImage = styled(Flex)`
  position: relative;
  height: ${spaceMixin(12)};
`;

export function CurrentIncomeOverview({ answers, isReadOnly }: Props) {
  const { currentIncomes } = answers;
  return (
    currentIncomes &&
    currentIncomes.length > 0 && (
      <CurrentIncomes
        answers={answers}
        currentIncomes={currentIncomes as Array<CurrentIncome>}
        isReadOnly={isReadOnly}
      />
    )
  );
}

function CurrentIncomes({ answers, currentIncomes, isReadOnly }: Props & { currentIncomes: Array<CurrentIncome> }) {
  const answersForFields = getAnswersForFields({ fieldIds: fieldsInStep, answers }) as { [key: string]: Array<string> };

  const totalIncome = currentIncomes.reduce(
    (total, currentIncome) => total + getNumberFromString(currentIncome.amount),
    0,
  );

  return (
    <>
      <Heading level={6} marginY="xs">
        Total monthly income
      </Heading>
      <Paragraph>{getCurrencyDisplay(`${totalIncome}`)}/mo</Paragraph>
      {currentIncomes &&
        currentIncomes.map((currentIncome, index) => {
          const type = get(answersForFields[FIELD_IDS.CURRENT_INCOMES_TYPE], `[${index}]`);
          const amount = get(answersForFields[FIELD_IDS.CURRENT_INCOMES_AMOUNT], `[${index}]`);
          const title = get(answersForFields[FIELD_IDS.CURRENT_INCOMES_TITLE], `[${index}]`);
          const note = get(answersForFields[FIELD_IDS.CURRENT_INCOMES_NOTE], `[${index}]`);

          const organization = get(answersForFields[FIELD_IDS.CURRENT_INCOMES_ORGANIZATION], `[${index}]`);
          const startDate = get(answersForFields[FIELD_IDS.CURRENT_INCOMES_START_DATE], `[${index}]`);
          const employerName = get(answersForFields[FIELD_IDS.CURRENT_INCOMES_EMPLOYER_NAME], `[${index}]`);
          const employerEmail = get(answersForFields[FIELD_IDS.CURRENT_INCOMES_EMPLOYER_EMAIL], `[${index}]`);
          const employerPhone = get(answersForFields[FIELD_IDS.CURRENT_INCOMES_EMPLOYER_PHONE], `[${index}]`);

          const documents = get(answersForFields[FIELD_IDS.CURRENT_INCOMES_DOCUMENTS], `[${index}]`) as unknown as
            | Array<Document>
            | undefined;

          return (
            <Fragment key={currentIncome?.id ?? index}>
              <Divider marginY="md" />
              <Heading level={6} as="h5" marginY="xs" fontColor="textMedium">
                Income source #{index + 1}
              </Heading>
              <Paragraph>{amount ? `${amount}/mo` : 'Amount needed'}</Paragraph>
              {type && title && (
                <>
                  <Heading level={6} marginY="xs">
                    {getIncomeTitle(type as IncomeType)}
                  </Heading>
                  <Paragraph>{title}</Paragraph>
                </>
              )}
              {documents && documents.length > 0 && (
                <>
                  <Heading level={6} marginY="xs">
                    Income documents
                  </Heading>
                  <Grid display="grid" gridTemplateColumns="repeat(10, 1fr)" gap="xs" marginY="md">
                    {documents
                      .filter((document: Document) => !!document.id && document.previewUrl)
                      .map((document: Document) => (
                        <Grid
                          gridColumnEnd={{ default: 'span 10', md_gte: 'span 5', xl_gte: 'span 2' }}
                          key={document.id}
                        >
                          <FlexImage flexDirection="column" display="flex" alignItems="center">
                            <Image
                              src={document.previewUrl as string}
                              alt={`Preview image for ${document.fileName}`}
                              fill
                              style={{
                                maxWidth: '100%',
                                objectFit: 'contain',
                              }}
                            />
                          </FlexImage>
                        </Grid>
                      ))}
                  </Grid>
                  {documents.length > MAX_DOCUMENTS_PER_INCOME && !isReadOnly && (
                    <Paragraph fontColor="red500" fontType="bodySmall">
                      A maximum of {MAX_DOCUMENTS_PER_INCOME} income documents per income source is allowed
                    </Paragraph>
                  )}
                </>
              )}
              {organization && (
                <>
                  <Heading level={6} marginY="xs">
                    Employer
                  </Heading>
                  <Paragraph>{organization}</Paragraph>
                </>
              )}
              {startDate && (
                <>
                  <Heading level={6} marginY="xs">
                    Started on
                  </Heading>
                  <Paragraph>{getFormattedDateValue(startDate)}</Paragraph>
                </>
              )}
              {(employerName || employerEmail || employerPhone) && (
                <>
                  <Heading level={6} marginY="xs">
                    Employer contact info
                  </Heading>
                  {employerName && <Paragraph>{employerName}</Paragraph>}
                  {employerEmail && <Paragraph>{employerEmail}</Paragraph>}
                  {employerPhone && <Paragraph>{getPhoneNumberScreenFormat(employerPhone)}</Paragraph>}
                </>
              )}
              {note && (
                <>
                  <Heading level={6} marginY="xs">
                    Additional Information
                  </Heading>
                  <Paragraph>{note}</Paragraph>
                </>
              )}
            </Fragment>
          );
        })}
    </>
  );
}
